import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import axios from "../../../services/axiosInstance";
import useTheme from "../../../components/web/layoutscommon/DarkModeStorage";
import btorageBooster from "../../../assets/img/btorageBooster.png";
import {saveStoreLink, clearStoreLink} from "../../../services/storeLink";
import StoreShell from "./StoreShell";
import {Btn} from "./StoreUI";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  쇼핑몰 상품연동 진입 페이지
 * ════════════════════════════════════════════════════════════════════════════════
 *  경로 : https://btorage.com/store/link?t={연동토큰}
 *
 *  [상황별 이동 처리]
 *   상황1  상점사 웹(PC)          -> 비토리지 웹(PC) 주문서로 이동
 *   상황2  상점사 웹(Mobile)      -> 비토리지 앱 설치 시 앱 주문서, 미설치 시 반응형 웹 주문서
 *   상황3  상점사 앱(AOS/iOS)     -> 위와 동일 (외부 브라우저로 열리므로 상황2와 동일하게 처리)
 *
 *  [앱 설치여부 판별]
 *   커스텀 스킴(btorage://StoreLinkPg?t=...) 으로 이동을 시도한 뒤,
 *   일정 시간 안에 페이지가 백그라운드로 전환되지 않으면 미설치로 간주하고 웹으로 폴백한다.
 *   (앱이 열리면 브라우저가 백그라운드로 가면서 visibilitychange/pagehide 가 발생한다)
 *
 *  [로그인 상태]
 *   - 로그인  : 주문서 최초작성 페이지로 이동
 *   - 로그아웃 : 로그인 페이지로 이동. 연동토큰은 localStorage 에 보관되어 로그인/가입 후에도 유지된다.
 * ════════════════════════════════════════════════════════════════════════════════
 */
function StoreLinkEntryPg() {

    const [theme] = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const linkToken = searchParams.get('t');

    const [status, setStatus] = useState('loading'); // loading | expired | moving
    const [companyName, setCompanyName] = useState('');
    const movedRef = useRef(false);

    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);

    useEffect(() => {
        let alive = true;

        const run = async () => {
            if (!linkToken) {
                setStatus('expired');
                return;
            }

            // 1) 연동데이터 유효성 확인
            let data = null;
            try {
                const res = await axios.get('/storeLink/linkedData', {params: {t: linkToken}});
                data = res.data;
            } catch (e) {
                if (!alive) return;
                setStatus('expired');
                return;
            }
            if (!alive) return;
            if (!data) {
                setStatus('expired');
                return;
            }

            setCompanyName(data.companyName || '');

            // 2) 연동토큰 보관 (로그인/회원가입을 거쳐도 유지)
            saveStoreLink(linkToken, data.companyName || '');

            // 3) 유입기록 (같은 고객/같은 날은 1회만 카운트)
            const LCAT = localStorage.getItem('AToken');
            const SSAT = sessionStorage.getItem('AToken');
            let mbNum = null;
            if (LCAT || SSAT) {
                try {
                    const me = await axios.get('/order/selectTokenMember', {
                        headers: {Authorization: LCAT || SSAT},
                    });
                    mbNum = me.data;
                } catch (e) { /* 비로그인으로 간주 */ }
            }
            axios.post('/storeLink/recordInflow', {
                linkToken,
                mbNum: mbNum ? String(mbNum) : '',
                platform: isMobile ? 'WEB' : 'WEB',
            }).catch(() => {});

            if (!alive) return;
            setStatus('moving');

            // 4) 모바일이면 앱 우선 시도 -> 미설치 시 웹 폴백
            if (isMobile) {
                tryOpenApp(!!mbNum);
            } else {
                goWeb(!!mbNum);
            }
        };

        run();
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkToken]);

    /** 비토리지 앱으로 진입 시도 (미설치면 웹으로 폴백) */
    const tryOpenApp = (loggedIn) => {
        let fallbackTimer = null;

        const cancelFallback = () => {
            if (document.hidden) {
                // 앱이 열려 브라우저가 백그라운드로 전환됨 -> 폴백 취소
                clearTimeout(fallbackTimer);
                movedRef.current = true;
            }
        };

        document.addEventListener('visibilitychange', cancelFallback);
        window.addEventListener('pagehide', cancelFallback);

        // 커스텀 스킴으로 앱 실행 시도
        window.location.href = `btorage://StoreLinkPg?t=${encodeURIComponent(linkToken)}`;

        fallbackTimer = setTimeout(() => {
            document.removeEventListener('visibilitychange', cancelFallback);
            window.removeEventListener('pagehide', cancelFallback);
            if (movedRef.current || document.hidden) return;
            goWeb(loggedIn); // 앱 미설치 -> 반응형 웹 주문서
        }, 1600);
    };

    /** 비토리지 웹 주문서(또는 로그인)로 이동 */
    const goWeb = (loggedIn) => {
        if (movedRef.current) return;
        movedRef.current = true;

        if (loggedIn) {
            navigate('/UserOrderPg', {replace: true});
        } else {
            // 로그인/회원가입 후 자동으로 주문서로 돌아오도록 리디렉션 경로를 남긴다
            // (기존 UserLoginPg.js / UserJoinPg.js 가 사용하는 'redirectAfterLogin' 규격을 그대로 따른다)
            sessionStorage.setItem('redirectAfterLogin', '/UserOrderPg');
            navigate('/UserLoginPg', {replace: true});
        }
    };

    const retry = () => {
        clearStoreLink();
        window.close();
        // 창이 닫히지 않는 브라우저를 위해 홈으로
        setTimeout(() => navigate('/', {replace: true}), 200);
    };

    return (
        <StoreShell theme={theme}>
            <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-px-4 tw-py-12">
                <div className="st-glass st-rise tw-w-full tw-max-w-[420px] tw-px-8 tw-py-10 tw-text-center">

                    {/* 로고 — 여명 위에 떠 있는 작은 빛 */}
                    <div className="tw-relative tw-mb-7 tw-inline-flex tw-items-center tw-justify-center">
                        <span aria-hidden="true"
                              className="tw-absolute tw-h-[86px] tw-w-[86px] tw-rounded-full tw-animate-breathe"
                              style={{background: 'radial-gradient(circle, rgba(255,190,72,.45), transparent 66%)'}}/>
                        <img src={btorageBooster} alt="Btorage" className="st-logo st-logo-md tw-relative"/>
                    </div>

                    {status === 'expired' ? (
                        <>
                            <h1 className="st-display tw-text-[19px] tw-text-[color:var(--st-text)]">
                                연동정보가 만료되었습니다
                            </h1>
                            <p className="tw-mx-auto tw-mt-3 tw-max-w-[300px] tw-text-[13px] tw-leading-[1.9] tw-text-[color:var(--st-text-sub)] tw-break-keep">
                                쇼핑몰에서 비토리지 버튼을 다시 눌러주세요.
                                연동정보는 24시간 동안 유효합니다.
                            </p>
                            <Btn tone="dawn" size="lg" className="tw-mt-7 tw-w-full" onClick={retry}>확인</Btn>
                        </>
                    ) : (
                        <>
                            {/* 이동 중 — 세 점이 차례로 숨쉰다 */}
                            <div className="tw-mb-6 tw-flex tw-items-center tw-justify-center tw-gap-2">
                                {[0, 1, 2].map(i => (
                                    <span key={i} aria-hidden="true"
                                          className="tw-h-2.5 tw-w-2.5 tw-rounded-full tw-animate-breathe"
                                          style={{
                                              background: ['#FFBE48', '#CBB6FF', '#A8DDE4'][i],
                                              animationDelay: `${i * 0.35}s`,
                                          }}/>
                                ))}
                            </div>

                            <h1 className="st-display tw-text-[18px] tw-text-[color:var(--st-text)]">
                                상품을 가져오고 있습니다
                            </h1>
                            <p className="tw-mx-auto tw-mt-3 tw-max-w-[300px] tw-text-[13px] tw-leading-[1.9] tw-text-[color:var(--st-text-sub)] tw-break-keep">
                                {companyName ? `${companyName} 상품을 ` : ''}비토리지 주문서로 옮기는 중입니다.
                                잠시만 기다려주세요.
                            </p>
                        </>
                    )}
                </div>
            </div>
        </StoreShell>
    );
}

export default StoreLinkEntryPg
